import React, { Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Route, Routes, useLocation } from 'react-router';
import { SWRConfig } from 'swr';

import FullscreenLoading from 'components/FullscreenLoading/FullscreenLoading';
import StatusCodeErrorHandler from 'components/status-code-errors/status-code-error-handler';
import config from 'config/site.config';
import ExternalTrainingConfirmation from 'containers/ExternalTrainingConfirmation/ExternalTrainingConfirmation';
import ReadOnlyMessage from 'containers/InfoCenter/ReadOnlyMessage/ReadOnlyMessage';
import useCurrentUserAndSettings, {
  revalidateUserAtom,
} from 'hooks/useCurrentUserAndSettings';
import { getPublic } from 'libs/apiClient';
import { signinSilentShared } from 'libs/auth';
import { isLoadingOidcUserAtom, oidcUserAtom } from 'libs/authState';
import { LocalStorageManager } from 'libs/utils';

import ReadOnlyAlert from '../Alerts/ReadOnlyAlert/ReadOnlyAlert';

const { legacyWebBaseUrl } = config;

const PublicRoot = () => {
  const location = useLocation();
  const revalidateUser = useSetAtom(revalidateUserAtom);
  const oidcUser = useAtomValue(oidcUserAtom);
  const isLoadingOidcUser = useAtomValue(isLoadingOidcUserAtom);
  const { currentUser } = useCurrentUserAndSettings();

  const getReturnUrl = () => {
    let returnTo = null;
    if (location && location.pathname) {
      const originalPath = location.pathname;
      if (originalPath !== '/' && originalPath !== '') {
        returnTo = location.pathname + (location.search ? location.search : '');
      }
    }

    return returnTo;
  };

  React.useEffect(() => {
    if (!isLoadingOidcUser) {
      if (!oidcUser) {
        LocalStorageManager.setRedirectUrlOnReturn(getReturnUrl());
        if (document.referrer.includes(legacyWebBaseUrl)) {
          signinSilentShared()
            .then(() => {
              revalidateUser();
            })
            .catch(() => {
              // Silent renew failed — user will need to log in via the main app
            });
        }
      } else {
        revalidateUser();
      }
    }
  }, [isLoadingOidcUser, oidcUser]);

  React.useEffect(() => {
    if (currentUser) {
      Sentry.setUser({
        id: currentUser.userCompanyProfileId,
        username: currentUser.name,
      });
    }
  }, [currentUser]);

  return (
    <SWRConfig
      value={{
        fetcher: (...args) => getPublic(...args).then((res) => res.json()),
      }}
    >
      <Suspense fallback={<FullscreenLoading />}>
        <StatusCodeErrorHandler>
          <Routes>
            <Route
              path="/info-message/:messageId"
              element={<ReadOnlyMessage />}
            />
            <Route path="/alerts/:alertId" element={<ReadOnlyAlert />} />
            <Route
              path="/external-training-confirm/:trainingId"
              element={<ExternalTrainingConfirmation />}
            />
          </Routes>
        </StatusCodeErrorHandler>
      </Suspense>
    </SWRConfig>
  );
};

export default PublicRoot;
